<script setup lang="ts">
import { computed, ref } from "vue";
import { VueSignaturePad } from "@selemondev/vue3-signature-pad";

import Container from "@/Components/UI/Grid/Container.vue";
import Column from "@/Components/UI/Grid/Column.vue";
import TcButton from "@/Components/UI/TcButton.vue";
import TcIcon from "@/Components/Extend/TcIcon.vue";
import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

export type SignatureData = {
  mimeType: string;
  data: string;
};

export interface SignatureProps {
  disabled?: boolean;
  format?: "image/jpeg" | "image/svg";
  texts?: {
    save?: string;
    clear?: string;
  };
  height?: number;
}

const emit = defineEmits<{
  (e: "signed", value?: SignatureData): void;
}>();

const props = withDefaults(defineProps<SignatureProps>(), {
  disabled: false,
  format: "image/jpeg",
  height: 400,
});

const signature = ref<typeof VueSignaturePad>();

const texts = computed(() => ({
  save: "Save",
  clear: "Clear",
  ...(hasValue(props.texts) ? props.texts : {}),
}));

const handleSave = () => {
  if (signature.value?.isCanvasEmpty()) return;

  const base64 = signature.value!.saveSignature(props.format);

  if (!isValidString(base64)) {
    emit("signed");

    return;
  }

  const data = base64.split(",")[1];

  if (hasValue(data)) {
    emit("signed", { mimeType: props.format, data });
  } else {
    emit("signed");
  }
};

const handleClear = () => {
  return signature.value!.clearCanvas();
};

const handleUndo = () => {
  return signature.value!.undo();
};
</script>

<template>
  <div class="signature">
    <div class="signature__header">
      <slot name="header" v-bind="{ handleUndo, handleSave, handleClear }">
        <TcIcon class="signature__header__icon" name="Undo" />
      </slot>
    </div>

    <div class="signature__content">
      <VueSignaturePad
        ref="signature"
        width="100%"
        :height="`${props.height}px`"
        :options="{ penColor: 'black', backgroundColor: 'white' }"
        :maxWidth="2"
        :minWidth="2"
      />
    </div>

    <div class="signature__footer">
      <slot name="actions" v-bind="{ handleUndo, handleSave, handleClear }">
        <Container columns="auto auto" justify="space-between">
          <Column>
            <TcButton type="button" @click="handleSave">{{ texts.save }}</TcButton>
          </Column>

          <Column>
            <TcButton variant="outlined" type="button" @click="handleClear">
              {{ texts.clear }}
            </TcButton>
          </Column>
        </Container>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.signature {
  @include radius(m);

  position: relative;

  &__header {
    position: absolute;
    top: $space-2;
    left: $space-2;

    &__icon {
      cursor: pointer;
    }
  }

  &__content {
    @include radius(s);
    @include colors.border(content, mid, true);

    overflow: none;
    border: 1px solid;
  }

  &__footer {
    @include sp.margin(t, 4);
  }
}
</style>

<style lang="scss">
.signature {
  &__content {
    canvas {
      @include radius(s);
    }
  }
}
</style>
